// @ts-strict-ignore
import { CommonModule } from "@angular/common";
import { Component, OnInit } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { InputNumberModule } from "primeng/inputnumber";
import { InputTextModule } from "primeng/inputtext";
import { RadioButtonModule } from "primeng/radiobutton";
import { TableModule } from "primeng/table";
import { ErrorCheck } from "src/app/shared/models/error-check.model";
import { ProgressCheck } from "src/app/shared/models/progress-check.model";
import { SharedDirectivesModule } from "src/app/shared/shared-directives.module";
import { ErrorMessageComponent } from "src/app/modules/esim/shared/components/error-message/error-message.component";
import * as C from "../../../../../../shared/constants/globals.constants";
import { EWizDynamicBaseClassComponent } from "src/app/modules/esim/shared/classes/ewiz-dynamic.class";
import { DataService } from "src/app/shared/services/data.service";
import * as ProfileModel from "src/app/shared/models/profile.model";

@Component({
  selector: C.EWIZ_PUK_CONFIGURATION,
  templateUrl: "./puk-configuration.component.html",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    InputNumberModule,
    InputTextModule,
    RadioButtonModule,
    TableModule,
    SharedDirectivesModule,
    ErrorMessageComponent
  ]
})
export class EWizPukConfigurationComponent extends EWizDynamicBaseClassComponent implements OnInit, ErrorCheck, ProgressCheck {
  constructor(private dataService: DataService) {
    super();
  }

  filterPinTypes = {
    hasPuk: { value: "true" }
  };

  ngOnInit() {
    this.errorCheck(this.profileValues, this.dataService);
  }

  errorCheck(values: ProfileModel.ProfileDto, dataService: DataService) {
    // Always reset error values to default state prior to executing check
    this.errorFound = false;
    this.errorMessage = "";

    // All PIN Values must have a length equal to the PIN Size
    for (const pin of values.pins) {
      if (+pin.type === 1) {
        if (dataService.isNullOrEmpty(pin.puk.size)) {
          this.errorMessage = $localize`PUK Size values must be entered`;
          this.errorFound = true;
        } else if (!dataService.valueInRange(pin.puk.size, 4, 8)) {
          this.errorMessage = $localize`PUK Size values must be within a range of 4 and 8`;
          this.errorFound = true;
        }
      }

      if (
        +pin.type === 1 &&
        +pin.puk.valueGeneration === 2 &&
        pin.puk.size &&
        pin.puk.value &&
        `${pin.puk.value}`.replace(/_/g, "").length !== +pin.puk.size
      ) {
        this.errorMessage = $localize`PUK Value lengths must match their specified PUK Size`;
        this.errorFound = true;
      }
    }

    // Additional conditions that only apply to PIN type 1/2
    for (const pin of values.pins) {
      if (+pin.type === 1) {
        // All Max Attempts must be entered and within range
        if (dataService.isNullOrEmpty(pin.puk.maxAttempts)) {
          this.errorMessage = $localize`Max Attempt values must be entered`;
          this.errorFound = true;
        } else if (!dataService.valueInRange(pin.puk.maxAttempts, 0, 15)) {
          this.errorMessage = $localize`Max Attempt values must be within a range of 0 and 15`;
          this.errorFound = true;
        }

        // All Retries Left must be entered and within range
        if (dataService.isNullOrEmpty(pin.puk.retryNumberLeft)) {
          this.errorMessage = $localize`Retries Left values must be entered`;
          this.errorFound = true;
        } else if (!dataService.valueInRange(pin.puk.retryNumberLeft, 0, 15)) {
          this.errorMessage = $localize`Retries Left values must be within a range of 0 and 15`;
          this.errorFound = true;
        } else if (!dataService.isNullOrEmpty(pin.puk.maxAttempts) && +pin.puk.retryNumberLeft > +pin.puk.maxAttempts) {
          this.errorMessage = $localize`Retries Left values must not exceed their Max Attempts`;
          this.errorFound = true;
        }
      }
    }

    return this.errorFound;
  }

  progressCheck(values: ProfileModel.ProfileDto, dataService: DataService) {
    for (const pin of values.pins) {
      if (+pin.type === 1) {
        if (dataService.isNullOrEmpty(pin.puk.size)) {
          return false;
        } else if (+pin.puk.valueGeneration === 2 && dataService.isNullOrEmpty(pin.puk.value)) {
          return false;
        } else if (dataService.isNullOrEmpty(pin.puk.maxAttempts)) {
          return false;
        } else if (dataService.isNullOrEmpty(pin.puk.retryNumberLeft)) {
          return false;
        }
      }
    }

    return true;
  }

  generatePinValue(pinObject) {
    pinObject.puk.value = this.getRandomPinBySize(pinObject.puk.size);

    this.handleFormEvents();
  }

  getRandomPinBySize(size) {
    let newPin = "";

    if (size) {
      for (let i = 0, ix = +size; i < ix; i++) {
        newPin += this.dataService.generateRandomInteger(0, 9);
      }
    }

    return newPin;
  }
}
